import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface EnrollmentStatusAuditItem {
  id: number;
  participantName: string;
  participantDocumentType: string;
  participantDocumentId: string;
  projectMnemonic: string;
  projectName: string;
  status: string;
  autoGeneratePaymentLinks: boolean;
  createdAt: string | null;
}

export interface CorporateEnrollmentStatusAuditItem {
  id: number;
  companyName: string;
  companyDocumentType: string;
  companyDocumentId: string;
  projectMnemonic: string;
  projectName: string;
  status: string;
  autoGeneratePaymentLinks: boolean;
  createdAt: string | null;
  participantCount: number;
}

export interface PagedResultDTO<T> {
  data: T[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
}

export interface BulkFixAutoGenerateResult {
  updated: number;
  failedIds: number[];
}

// ===== Bulk fix async batch system =====

export type BulkFixScope = 'individual' | 'corporate';
export type BulkFixAction = 'SetAutoGenerate' | 'SetStatus';

export interface EnrollmentAuditBulkFixRequest {
  scope: BulkFixScope;
  action: BulkFixAction;
  valueBool?: boolean | null;
  valueStatus?: number | null;
  comment: string;
  explicitIds?: number[] | null;
  filterStatus?: number | null;
  filterAutoGenerate?: boolean | null;
  filterProjectId?: number | null;
  filterSearch?: string | null;
}

export interface EnrollmentAuditBulkFixPreviewTarget {
  id: number;
  display: string;
  currentStatus: string;
  currentAutoGenerate: boolean;
}

export interface EnrollmentAuditBulkFixPreview {
  totalOperations: number;
  sampleTargets: EnrollmentAuditBulkFixPreviewTarget[];
}

export interface StartEnrollmentAuditBulkFixResponse {
  batchId: string;
  totalItems: number;
  status: string;
}

export interface EnrollmentAuditBulkFixItem {
  id: number;
  targetEnrollmentId: number | null;
  targetCorporateEnrollmentId: number | null;
  previousValue: string | null;
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Skipped';
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  affectedRows: number | null;
  error: string | null;
}

export interface EnrollmentAuditBulkFixBatchDetail {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'Pending' | 'Running' | 'Completed' | 'Failed' | 'Cancelled';
  startedByUserId: string | null;
  scope: BulkFixScope;
  action: BulkFixAction;
  targetValue: string;
  comment: string;
  totalItems: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  items: EnrollmentAuditBulkFixItem[];
}

export interface AuditStatusParams {
  status?: number | null;
  autoGenerate?: boolean | null;
  projectId?: number | null;
  search?: string | null;
  pageNumber?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class EnrollmentStatusAuditService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getIndividualAudit(params: AuditStatusParams): Observable<PagedResultDTO<EnrollmentStatusAuditItem>> {
    let p = new HttpParams()
      .set('pageNumber', params.pageNumber ?? 1)
      .set('pageSize', params.pageSize ?? 20);
    if (params.status != null) p = p.set('status', params.status);
    if (params.autoGenerate != null) p = p.set('autoGenerate', params.autoGenerate);
    if (params.projectId != null) p = p.set('projectId', params.projectId);
    if (params.search) p = p.set('search', params.search);
    return this.http.get<PagedResultDTO<EnrollmentStatusAuditItem>>(
      `${this.baseUrl}/enrollment/audit/status`, { params: p });
  }

  getCorporateAudit(params: AuditStatusParams): Observable<PagedResultDTO<CorporateEnrollmentStatusAuditItem>> {
    let p = new HttpParams()
      .set('pageNumber', params.pageNumber ?? 1)
      .set('pageSize', params.pageSize ?? 20);
    if (params.status != null) p = p.set('status', params.status);
    if (params.autoGenerate != null) p = p.set('autoGenerate', params.autoGenerate);
    if (params.projectId != null) p = p.set('projectId', params.projectId);
    if (params.search) p = p.set('search', params.search);
    return this.http.get<PagedResultDTO<CorporateEnrollmentStatusAuditItem>>(
      `${this.baseUrl}/corporateenrollment/audit/status`, { params: p });
  }

  setIndividualAutoGenerate(id: number, value: boolean): Observable<boolean> {
    return this.http.patch<boolean>(
      `${this.baseUrl}/enrollment/${id}/auto-generate`, { value });
  }

  setCorporateAutoGenerate(id: number, value: boolean): Observable<boolean> {
    return this.http.patch<boolean>(
      `${this.baseUrl}/corporateenrollment/${id}/auto-generate`, { value });
  }

  bulkFixIndividual(ids: number[]): Observable<BulkFixAutoGenerateResult> {
    return this.http.post<BulkFixAutoGenerateResult>(
      `${this.baseUrl}/enrollment/audit/bulk-fix-auto-generate`, { ids });
  }

  bulkFixCorporate(ids: number[]): Observable<BulkFixAutoGenerateResult> {
    return this.http.post<BulkFixAutoGenerateResult>(
      `${this.baseUrl}/corporateenrollment/audit/bulk-fix-auto-generate`, { ids });
  }

  // ===== Async bulk fix batch =====

  previewBulkFix(req: EnrollmentAuditBulkFixRequest): Observable<EnrollmentAuditBulkFixPreview> {
    return this.http.post<EnrollmentAuditBulkFixPreview>(
      `${this.baseUrl}/enrollmentauditbulkfix/preview`, req);
  }

  startBulkFix(req: EnrollmentAuditBulkFixRequest): Observable<StartEnrollmentAuditBulkFixResponse> {
    return this.http.post<StartEnrollmentAuditBulkFixResponse>(
      `${this.baseUrl}/enrollmentauditbulkfix/start`, req);
  }

  getBulkFixBatch(batchId: string): Observable<EnrollmentAuditBulkFixBatchDetail> {
    return this.http.get<EnrollmentAuditBulkFixBatchDetail>(
      `${this.baseUrl}/enrollmentauditbulkfix/${batchId}`);
  }

  cancelBulkFix(batchId: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}/enrollmentauditbulkfix/${batchId}/cancel`, {});
  }
}
